// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@/components/ui';
import { Check, ChevronDown, Search, Folder, Plus } from "lucide-react";
import type { WorkspaceInfo } from "../types/workspace";


interface WorkspaceSwitcherProps {
  accessibleWorkspaces: WorkspaceInfo[];
  selectedOrganizationId?: string;
  selectedWorkspaceId?: string;
  selectWorkspace: (orgId: string, wsId: string) => void;
  isLoading?: boolean;
  onCreateWorkspace?: () => void;
  canCreateWorkspace?: boolean;
  recentWorkspaceIds?: string[];
}

interface AppGroup {
  displayName: string;
  appWorkspace?: WorkspaceInfo;
  workspaces: WorkspaceInfo[];
}

interface GroupedWorkspaces {
  system: WorkspaceInfo[];
  applications: Record<string, AppGroup>;
}

type FlatItem =
  | { type: "section"; label: string; groupId?: string; count?: number; accent?: string; expanded?: boolean }
  | { type: "workspace"; workspace: WorkspaceInfo; isAdminWorkspace?: boolean };

export function WorkspaceSwitcher({
  accessibleWorkspaces = [],
  selectedOrganizationId,
  selectedWorkspaceId,
  selectWorkspace,
  isLoading = false,
  onCreateWorkspace,
  canCreateWorkspace = false,
  recentWorkspaceIds = [],
}: WorkspaceSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [groupExpansion, setGroupExpansion] = useState<Record<string, boolean>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Find the currently selected workspace
  const currentWorkspace = accessibleWorkspaces.find(
    (ws) => ws.orgId === selectedOrganizationId && ws.wsId === selectedWorkspaceId
  );

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Register ⌘K / Ctrl+K hotkey
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (modifier && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Group workspaces by application
  const grouped = useMemo<GroupedWorkspaces>(() => {
    const result: GroupedWorkspaces = { system: [], applications: {} };

    for (const ws of accessibleWorkspaces) {
      const wsType = ws.workspaceType || "workspace";
      const app = ws.application || "default";
      const displayName = ws.applicationDisplayName || formatAppName(app);

      if (wsType === "system") {
        result.system.push(ws);
      } else if (wsType === "application") {
        if (!result.applications[app]) {
          result.applications[app] = { displayName, workspaces: [] };
        }
        result.applications[app].appWorkspace = ws;
        // Update display name from app workspace (most authoritative)
        result.applications[app].displayName = displayName;
      } else {
        if (!result.applications[app]) {
          result.applications[app] = { displayName, workspaces: [] };
        }
        result.applications[app].workspaces.push(ws);
      }
    }

    return result;
  }, [accessibleWorkspaces]);

  // Build flat list for keyboard navigation
  const flatList = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = [];
    const lowerQuery = query.toLowerCase();

    const matchesQuery = (ws: WorkspaceInfo) => {
      if (!query) return true;
      const name = (ws.name || ws.wsId).toLowerCase();
      const app = (ws.application || "").toLowerCase();
      const appDisplay = (ws.applicationDisplayName || "").toLowerCase();
      return name.includes(lowerQuery) || app.includes(lowerQuery) || appDisplay.includes(lowerQuery);
    };

    // Recent workspaces
    if (!query && recentWorkspaceIds.length > 0) {
      const recentWs = recentWorkspaceIds
        .map((id) => accessibleWorkspaces.find((w) => `${w.orgId}/${w.wsId}` === id))
        .filter((w): w is WorkspaceInfo => !!w)
        .slice(0, 3);

      if (recentWs.length > 0) {
        items.push({ type: "section", label: "Recently Used" });
        for (const ws of recentWs) {
          items.push({ type: "workspace", workspace: ws });
        }
      }
    }

    // System workspaces
    const filteredSystem = grouped.system.filter(matchesQuery);
    if (filteredSystem.length > 0) {
      items.push({ type: "section", label: "System" });
      for (const ws of filteredSystem) {
        items.push({ type: "workspace", workspace: ws, isAdminWorkspace: true });
      }
    }

    // Application groups
    const apps = Object.keys(grouped.applications).sort();
    for (const app of apps) {
      const group = grouped.applications[app];
      const filteredApp = group.appWorkspace && matchesQuery(group.appWorkspace);
      const filteredWs = group.workspaces.filter(matchesQuery);

      if (filteredApp || filteredWs.length > 0) {
        const isCurrentApp = currentWorkspace
          ? (currentWorkspace.application || 'default') === app
          : false;
        const expanded = Boolean(query) || (groupExpansion[app] ?? isCurrentApp);
        items.push({
          type: "section",
          label: group.displayName,
          groupId: app,
          count: group.workspaces.length + (group.appWorkspace ? 1 : 0),
          accent: applicationAccent(app),
          expanded,
        });

        if (expanded && filteredApp && group.appWorkspace) {
          items.push({
            type: "workspace",
            workspace: group.appWorkspace,
            isAdminWorkspace: true,
          });
        }

        if (expanded) {
          for (const ws of filteredWs) {
            items.push({ type: "workspace", workspace: ws });
          }
        }
      }
    }

    return items;
  }, [
    query,
    accessibleWorkspaces,
    grouped,
    recentWorkspaceIds,
    currentWorkspace,
    groupExpansion,
  ]);

  // Selectable items only (exclude section headers)
  const selectableIndices = useMemo(
    () =>
      flatList
        .map((item, i) => (item.type !== "section" ? i : -1))
        .filter((i) => i >= 0),
    [flatList]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentSelectableIndex = selectableIndices.indexOf(selectedIndex);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex = selectableIndices[currentSelectableIndex + 1];
        if (nextIndex !== undefined) {
          setSelectedIndex(nextIndex);
          scrollIntoView(nextIndex);
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prevIndex = selectableIndices[currentSelectableIndex - 1];
        if (prevIndex !== undefined) {
          setSelectedIndex(prevIndex);
          scrollIntoView(prevIndex);
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatList[selectedIndex];
        if (item && item.type !== "section") {
          handleSelect(item);
        }
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    },
    [flatList, selectedIndex, selectableIndices]
  );

  const scrollIntoView = (index: number) => {
    const el = listRef.current?.querySelector(`[data-index="${index}"]`);
    el?.scrollIntoView({ block: "nearest" });
  };

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [query, selectableIndices]);

  const handleSelect = (item: FlatItem) => {
    if (item.type === "workspace") {
      selectWorkspace(item.workspace.orgId, item.workspace.wsId);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-11 w-full gap-2.5 rounded-[10px] border-[#e2e8f0] px-3.5 text-[15px] font-medium leading-[normal] text-[#0f172a] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[background,border-color] duration-[150ms] ease-[ease] hover:border-[#cbd5e1] hover:bg-[#f8fafc] sm:w-auto sm:max-w-lg',
            open && 'border-[#94a3b8] bg-[#f8fafc]',
          )}
        >
          <Folder className="h-[17px] w-[17px] shrink-0 text-[#475569]" />
          <span className="truncate">
            {isLoading ? "Loading..." : (currentWorkspace?.name || "Select Workspace")}
          </span>
          <ChevronDown className={cn(
            'h-4 w-4 shrink-0 text-[#64748b] transition-transform duration-[150ms] ease-[ease]',
            open && 'rotate-180',
          )} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="docreview-workspace-menu flex w-[440px] max-w-[80vw] flex-col overflow-hidden rounded-[14px] border-[#e2e8f0] p-0 shadow-[0_16px_40px_rgba(15,23,42,0.18),0_3px_8px_rgba(15,23,42,0.06)]"
        align="start"
        sideOffset={6}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex shrink-0 items-center gap-2.5 border-b border-[#eef2f6] px-[15px] py-[13px]">
          <Search className="h-[17px] w-[17px] shrink-0 text-[#64748b]" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent px-0.5 py-px text-[15px] leading-[normal] text-[#0f172a] outline-none placeholder:text-[#94a3b8]"
            placeholder="Search all workspaces…"
            data-testid="ws-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="hidden shrink-0 rounded-md bg-[#f1f5f9] px-[7px] py-[3px] text-xs font-semibold leading-[normal] text-[#64748b] sm:block">
            ⌘K
          </kbd>
        </div>

        {currentWorkspace && !query && (
          <div className="flex shrink-0 items-center gap-[11px] border-b border-[#eef2f6] bg-[#f8fafc] px-[15px] py-3">
            <span className={cn('flex size-[34px] shrink-0 items-center justify-center rounded-[9px] text-white', applicationAccent(currentWorkspace.application ?? 'default'))}>
              <Folder className="h-[17px] w-[17px]" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[11px] font-semibold leading-[normal] uppercase tracking-[0.04em] text-[#94a3b8]">
                Current · {currentWorkspace.applicationDisplayName ?? formatAppName(currentWorkspace.application ?? 'workspace')}
              </span>
              <span className="block truncate text-[15px] font-semibold leading-[normal] text-[#0f172a]">
                {currentWorkspace.name || currentWorkspace.wsId}
              </span>
            </span>
          </div>
        )}

        {/* Workspace list */}
        <div ref={listRef} data-testid="ws-workspace-list" className="max-h-[360px] overflow-y-auto p-1.5">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              Loading workspaces...
            </div>
          ) : flatList.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              No workspaces found
            </div>
          ) : (
            flatList.map((item, index) => {
              if (item.type === "section") {
                return (
                  <button
                    type="button"
                    key={`section-${index}`}
                    onClick={() => {
                      if (!item.groupId) return
                      setGroupExpansion((current) => ({
                        ...current,
                        [item.groupId!]: !item.expanded,
                      }))
                    }}
                    className={cn(
                      'flex w-full items-center gap-[9px] rounded-lg px-2.5 py-[9px] text-xs font-bold leading-[normal] uppercase tracking-[0.05em] text-[#64748b] transition-colors duration-[120ms]',
                      item.groupId ? 'cursor-pointer hover:bg-[#f8fafc]' : 'cursor-default bg-[#f8fafc]',
                    )}
                  >
                    {item.groupId && (
                      <ChevronDown className={cn(
                        'h-3.5 w-3.5 text-[#94a3b8] transition-transform duration-[150ms] ease-[ease]',
                        !item.expanded && '-rotate-90',
                      )} />
                    )}
                    {item.accent && <span className={cn('size-[7px] rounded-sm', item.accent)} />}
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.count !== undefined && (
                      <span className="min-w-[22px] rounded-full bg-[#f1f5f9] px-[7px] py-0.5 text-center text-xs font-semibold text-[#94a3b8]">
                        {item.count}
                      </span>
                    )}
                  </button>
                );
              }

              if (item.type === "workspace") {
                const ws = item.workspace;
                const isSelected =
                  ws.orgId === selectedOrganizationId && ws.wsId === selectedWorkspaceId;
                const isHighlighted = index === selectedIndex;

                return (
                  <button
                    type="button"
                    key={`${ws.orgId}-${ws.wsId}`}
                    data-testid={`ws-item-${ws.wsId}`}
                    data-index={index}
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-[11px] rounded-lg py-[9px] pl-[30px] pr-2.5 text-left text-[15px] leading-[normal] transition-colors duration-[120ms]',
                      isSelected
                        ? 'bg-[#eff6ff] text-[#0f172a]'
                        : isHighlighted ? 'bg-[#f1f5f9]' : 'hover:bg-[#f1f5f9]',
                    )}
                  >
                    <Folder className={cn('h-4 w-4 shrink-0', isSelected ? 'text-[#2563eb]' : 'text-[#94a3b8]')} />
                    <span
                      className={cn(
                        "flex-1 truncate text-[15px]",
                        isSelected ? "font-semibold text-[#0f172a]" : "text-[#0f172a]"
                      )}
                    >
                      {ws.name || ws.wsId}
                    </span>
                    {!item.isAdminWorkspace && typeof ws.documentCount === "number" && (
                      <span className="text-xs font-medium text-[#94a3b8]">{ws.documentCount}</span>
                    )}
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-[#2563eb]" strokeWidth={2.5} />}
                  </button>
                );
              }

              return null;
            })
          )}
        </div>
        {canCreateWorkspace && onCreateWorkspace ? (
          <div className="flex shrink-0 items-center gap-4 border-t border-[#eef2f6] bg-white px-4 py-2.5">
            <button
              type="button"
              data-testid="ws-create-workspace"
              onClick={() => {
                onCreateWorkspace();
                setOpen(false);
              }}
              className="flex cursor-pointer items-center gap-2.5 rounded-md py-1 text-left text-[15px] font-semibold leading-[normal] text-[#2563eb] transition-colors duration-[120ms] hover:text-blue-700"
            >
              <Plus className="h-[17px] w-[17px]" strokeWidth={2.2} />
              <span>New Workspace</span>
            </button>
            <span className="min-w-0 flex-1 text-xs leading-4 text-[#94a3b8]">
              Vault is enabled per workspace in Settings
            </span>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function formatAppName(app: string): string {
  return app
    .replace(/[-_]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function applicationAccent(app: string): string {
  if (app.includes('i9')) return 'bg-violet-500'
  if (app.includes('legislative')) return 'bg-emerald-500'
  if (app.includes('software') || app.includes('srm')) return 'bg-cyan-500'
  return 'bg-blue-600'
}
